var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from 'react';
import { useCallback, useState } from 'react';
import { isElement } from 'react-is';
import { FieldTitle, InputHelperText, sanitizeInputRestProps, useChoices, useChoicesContext, useInput, useTranslate, } from 'react-admin';
import { Button, FormControl, FormHelperText, InputLabel, List, ListSubheader, styled, } from '@mui/material';
import ArrowLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowRightIcon from '@mui/icons-material/ChevronRight';
import clsx from 'clsx';
import { DualListInputItem } from './DualListInputItem';
import { DualListInputSkeleton } from './DualListInputSkeleton';
/**
 * An Input component displaying two list of selected or available items.
 * It allows multiple selections and uses an array of objects for the options.
 *
 * Pass possible options as an array of objects in the 'choices' attribute.
 *
 * By default, the options are built from:
 *  - the 'id' property as the option value,
 *  - the 'name' property an the option text
 * @example
 * const choices = [
 *    { id: 'programming', name: 'Programming' },
 *    { id: 'lifestyle', name: 'Lifestyle' },
 *    { id: 'photography', name: 'Photography' },
 * ];
 * <DualListInput source="tags" choices={choices} />
 *
 * You can also customize the properties to use for the option name and value,
 * thanks to the 'optionText' and 'optionValue' attributes.
 * @example
 * const choices = [
 *    { _id: 123, full_name: 'Leo Tolstoi', sex: 'M' },
 *    { _id: 456, full_name: 'Jane Austen', sex: 'F' },
 * ];
 * <DualListInput source="authors" choices={choices} optionText="full_name" optionValue="_id" />
 *
 * `optionText` also accepts a function, so you can shape the option text at will:
 * @example
 * const choices = [
 *    { id: 123, first_name: 'Leo', last_name: 'Tolstoi' },
 *    { id: 456, first_name: 'Jane', last_name: 'Austen' },
 * ];
 * const optionRenderer = choice => `${choice.first_name} ${choice.last_name}`;
 * <DualListInput source="authors" choices={choices} optionText={optionRenderer} />
 *
 * `optionText` also accepts a React Element, that will be cloned and receive
 * the related choice as the `record` prop. You can use Field components there.
 * @example
 * const choices = [
 *    { id: 123, first_name: 'Leo', last_name: 'Tolstoi' },
 *    { id: 456, first_name: 'Jane', last_name: 'Austen' },
 * ];
 * const FullNameField = ({ record }) => <span>{record.first_name} {record.last_name}</span>;
 * <DualListInput source="authors" choices={choices} optionText={<FullNameField />}/>
 *
 * The choices are translated by default, so you can use translation identifiers as choices:
 * @example
 * const choices = [
 *    { id: 'programming', name: 'myroot.tags.programming' },
 *    { id: 'lifestyle', name: 'myroot.tags.lifestyle' },
 *    { id: 'photography', name: 'myroot.tags.photography' },
 * ];
 */
export var DualListInput = function (props) {
    var addButton = props.addButton, _a = props.addButtonLabel, addButtonLabel = _a === void 0 ? 'ra-relationships.duallistinput.select' : _a, _b = props.availableItemsLabel, availableItemsLabel = _b === void 0 ? 'ra-relationships.duallistinput.availableItems' : _b, choicesProp = props.choices, className = props.className, _c = props.dense, dense = _c === void 0 ? true : _c, _d = props.disableValue, disableValue = _d === void 0 ? 'disabled' : _d, format = props.format, helperText = props.helperText, isFetchingProp = props.isFetching, isLoadingProp = props.isLoading, label = props.label, onBlur = props.onBlur, onChange = props.onChange, _e = props.optionText, optionText = _e === void 0 ? 'name' : _e, _f = props.optionValue, optionValue = _f === void 0 ? 'id' : _f, parse = props.parse, removeButton = props.removeButton, _g = props.removeButtonLabel, removeButtonLabel = _g === void 0 ? 'ra-relationships.duallistinput.unselect' : _g, resourceProp = props.resource, _h = props.selectedItemsLabel, selectedItemsLabel = _h === void 0 ? 'ra-relationships.duallistinput.selectedItems' : _h, sourceProp = props.source, translateChoice = props.translateChoice, validate = props.validate, variant = props.variant, rest = __rest(props, ["addButton", "addButtonLabel", "availableItemsLabel", "choices", "className", "dense", "disableValue", "format", "helperText", "isFetching", "isLoading", "label", "onBlur", "onChange", "optionText", "optionValue", "parse", "removeButton", "removeButtonLabel", "resource", "selectedItemsLabel", "source", "translateChoice", "validate", "variant"]);
    var translate = useTranslate();
    var _j = useChoicesContext({
        choices: choicesProp,
        isLoading: isLoadingProp,
        isFetching: isFetchingProp,
        resource: resourceProp,
        source: sourceProp,
    }), allChoices = _j.allChoices, isLoading = _j.isLoading, source = _j.source, resource = _j.resource;
    var getChoiceValue = useChoices({
        optionText: optionText,
        optionValue: optionValue,
        translateChoice: translateChoice,
    }).getChoiceValue;
    var _k = useInput(__assign({ format: format, onBlur: onBlur, onChange: onChange, parse: parse, resource: resource, source: source, validate: validate }, rest)), field = _k.field, isRequired = _k.isRequired, _l = _k.fieldState, error = _l.error, invalid = _l.invalid, isTouched = _l.isTouched, isDirty = _l.isDirty, isSubmitted = _k.formState.isSubmitted;
    // This handle the internal selection of items which can then be moved
    // from one list to the other
    var _m = useState([]), selectedItems = _m[0], setSelectedItems = _m[1];
    var _o = useState([]), availableSelectedItems = _o[0], setAvailableSelectedItems = _o[1];
    // Toggle the selection of a single item
    var handleToggleItemSelection = useCallback(function (event, item) {
        setSelectedItems(function (currentSelectedItems) {
            var isItemSelected = currentSelectedItems.some(function (selectedItem) {
                return getChoiceValue(selectedItem) === getChoiceValue(item);
            });
            if (isItemSelected) {
                return currentSelectedItems.filter(function (selectedItem) {
                    return getChoiceValue(selectedItem) !==
                        getChoiceValue(item);
                });
            }
            else {
                return __spreadArray(__spreadArray([], currentSelectedItems, true), [item], false);
            }
        });
    }, [getChoiceValue]);
    // Toggle the selection of a single available item
    var handleToggleAvailableItemSelection = useCallback(function (event, item) {
        setAvailableSelectedItems(function (currentSelectedItems) {
            var isItemSelected = currentSelectedItems.some(function (selectedItem) {
                return getChoiceValue(selectedItem) === getChoiceValue(item);
            });
            if (isItemSelected) {
                return currentSelectedItems.filter(function (selectedItem) {
                    return getChoiceValue(selectedItem) !==
                        getChoiceValue(item);
                });
            }
            else {
                return __spreadArray(__spreadArray([], currentSelectedItems, true), [item], false);
            }
        });
    }, [getChoiceValue]);
    var setInputValue = useCallback(function (value) {
        field.onChange(value);
        setSelectedItems([]);
        setAvailableSelectedItems([]);
    }, [field]);
    // Handler called when an item should be moved to the other list
    var handleMoveItem = useCallback(function (event, choice) {
        if ((field.value || []).some(function (value) { return value === getChoiceValue(choice); })) {
            setInputValue((field.value || []).filter(function (item) { return item !== getChoiceValue(choice); }));
            return;
        }
        setInputValue(__spreadArray(__spreadArray([], field.value, true), [getChoiceValue(choice)], false));
    }, [getChoiceValue, field, setInputValue]);
    // Handler called when the selected items should be added to the input value
    var handleAddItems = useCallback(function () {
        var currentSet = new Set(field.value || []);
        availableSelectedItems.forEach(function (item) {
            return currentSet.add(getChoiceValue(item));
        });
        setInputValue(Array.from(currentSet));
    }, [getChoiceValue, field, availableSelectedItems, setInputValue]);
    // Handler called when the selected items should be removed from the input value
    var handleRemoveItems = useCallback(function () {
        var newValue = (field.value || []).filter(function (value) {
            return !selectedItems.some(function (choice) { return getChoiceValue(choice) === value; });
        });
        setInputValue(newValue);
    }, [getChoiceValue, field, selectedItems, setInputValue]);
    return (React.createElement(Root, __assign({ fullWidth: true, margin: dense ? 'dense' : 'normal', className: clsx('ra-input', "ra-input-".concat(source), "ra-input-".concat(variant), className), error: (isDirty || isSubmitted) && invalid }, sanitizeInputRestProps(rest)),
        React.createElement(InputLabel, { className: DualListInputClasses.label, htmlFor: source, shrink: true, error: (isDirty || isSubmitted) && invalid },
            React.createElement(FieldTitle, { label: label, source: source, resource: resource, isRequired: isRequired })),
        React.createElement("div", { className: DualListInputClasses.main },
            React.createElement("div", null,
                React.createElement(ListSubheader, { component: "div", id: "available-items-title", className: DualListInputClasses.listHeader }, translate(availableItemsLabel, {
                    _: availableItemsLabel ===
                        'ra-relationships.duallistinput.availableItems'
                        ? 'Available items'
                        : availableItemsLabel,
                })),
                isLoading ? (React.createElement(DualListInputSkeleton, { className: DualListInputClasses.list })) : (React.createElement(List, { className: clsx(DualListInputClasses.list, DualListInputClasses.availableList), dense: dense, disablePadding: true, "aria-labelledby": "available-items-title", "aria-multiselectable": "true", role: "listbox" }, allChoices.map(function (choice) {
                    return !field.value.includes(getChoiceValue(choice)) ? (React.createElement(DualListInputItem, { key: getChoiceValue(choice), choice: choice, disableValue: disableValue, onMove: handleMoveItem, onToggleSelection: handleToggleAvailableItemSelection, optionText: optionText, optionValue: optionValue, selected: availableSelectedItems.some(function (selectedItem) {
                            return getChoiceValue(selectedItem) ===
                                getChoiceValue(choice);
                        }), translateChoice: translateChoice })) : null;
                })))),
            React.createElement("div", { className: DualListInputClasses.actions },
                isElement(addButton) ? (React.cloneElement(addButton, {
                    onClick: handleAddItems,
                    disabled: availableSelectedItems.length === 0,
                })) : (React.createElement(Button, { className: clsx(DualListInputClasses.button, DualListInputClasses.addButton), onClick: handleAddItems, endIcon: React.createElement(ArrowRightIcon, null), variant: addButton, disabled: availableSelectedItems.length === 0 }, translate(addButtonLabel, {
                    _: addButtonLabel ===
                        'ra-relationships.duallistinput.select'
                        ? 'Select'
                        : addButtonLabel,
                }))),
                isElement(removeButton) ? (React.cloneElement(removeButton, {
                    onClick: handleRemoveItems,
                    disabled: selectedItems.length === 0,
                })) : (React.createElement(Button, { className: clsx(DualListInputClasses.button, DualListInputClasses.removeButton), onClick: handleRemoveItems, startIcon: React.createElement(ArrowLeftIcon, null), variant: removeButton, disabled: selectedItems.length === 0 }, translate(removeButtonLabel, {
                    _: removeButtonLabel ===
                        'ra-relationships.duallistinput.unselect'
                        ? 'Unselect'
                        : removeButtonLabel,
                })))),
            React.createElement("div", null,
                React.createElement(ListSubheader, { component: "div", id: "selected-items-title", className: DualListInputClasses.listHeader }, translate(selectedItemsLabel, {
                    _: selectedItemsLabel ===
                        'ra-relationships.duallistinput.selectedItems'
                        ? 'Selected items'
                        : selectedItemsLabel,
                })),
                isLoading ? (React.createElement(DualListInputSkeleton, { className: DualListInputClasses.list })) : (React.createElement(List, { className: clsx(DualListInputClasses.list, DualListInputClasses.selectedList), dense: dense, disablePadding: true, "aria-labelledby": "selected-items-title", "aria-multiselectable": "true", role: "listbox" }, allChoices.map(function (choice) {
                    return field.value.includes(getChoiceValue(choice)) ? (React.createElement(DualListInputItem, { key: getChoiceValue(choice), choice: choice, disableValue: disableValue, onMove: handleMoveItem, onToggleSelection: handleToggleItemSelection, optionText: optionText, optionValue: optionValue, selected: selectedItems.some(function (selectedItem) {
                            return getChoiceValue(selectedItem) ===
                                getChoiceValue(choice);
                        }), translateChoice: translateChoice })) : null;
                }))))),
        React.createElement(FormHelperText, { error: (isTouched || isSubmitted) && invalid },
            React.createElement(InputHelperText, { touched: isTouched || isSubmitted, error: error === null || error === void 0 ? void 0 : error.message, helperText: helperText }))));
};
DualListInput.defaultProps = {
    defaultValue: [],
    source: '',
};
var PREFIX = 'RaDualListInput';
export var DualListInputClasses = {
    main: "".concat(PREFIX, "-main"),
    label: "".concat(PREFIX, "-label"),
    actions: "".concat(PREFIX, "-actions"),
    button: "".concat(PREFIX, "-button"),
    addButton: "".concat(PREFIX, "-addButton"),
    removeButton: "".concat(PREFIX, "-removeButton"),
    list: "".concat(PREFIX, "-list"),
    listHeader: "".concat(PREFIX, "-listHeader"),
    selectedList: "".concat(PREFIX, "-selectedList"),
    availableList: "".concat(PREFIX, "-availableList"),
};
var Root = styled(FormControl, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            paddingTop: theme.spacing(1.5)
        },
        _b["& .".concat(DualListInputClasses.main)] = {
            position: 'relative',
            display: 'flex',
        },
        _b["& .".concat(DualListInputClasses.label)] = {
            position: 'relative',
            zIndex: 2,
            transform: 'translate(12px, 0) scale(0.75)',
        },
        _b["&.ra-input-outlined .".concat(DualListInputClasses.label)] = {
            transform: 'translate(14px, 0) scale(0.75)',
        },
        _b["&.ra-input-standard .".concat(DualListInputClasses.label)] = {
            transform: 'translate(0, 0) scale(0.75)',
        },
        _b["&.ra-input-filled .".concat(DualListInputClasses.label)] = {
            transform: 'translate(12px, 0) scale(0.75)',
        },
        _b["& .".concat(DualListInputClasses.actions)] = {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
            justifyContent: 'center',
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
        },
        _b["& .".concat(DualListInputClasses.button)] = {
            justifyContent: 'space-between',
        },
        _b["& .".concat(DualListInputClasses.listHeader)] = {
            border: "solid 1px ".concat(theme.palette.divider),
            borderBottom: 'none',
            borderRadius: "".concat(parseFloat(theme.shape.borderRadius.toString()), "px ").concat(parseFloat(theme.shape.borderRadius.toString()), "px 0 0 "),
        },
        _b["& .".concat(DualListInputClasses.list)] = {
            minWidth: 256,
            height: 256,
            overflow: 'auto',
            border: "solid 1px ".concat(theme.palette.divider),
        },
        _b["& .".concat(DualListInputClasses.selectedList)] = {
            backgroundColor: theme.palette.background.paper,
        },
        _b["& .".concat(DualListInputClasses.availableList)] = {
            backgroundColor: theme.palette.background.paper,
        },
        _b);
});
